import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import BusinessNameTab from './brand-kit/BusinessNameTab';
import LogoDesignTab from './brand-kit/LogoDesignTab';
import EmailSignatureTab from './brand-kit/EmailSignatureTab';
import LetterheadTab from './brand-kit/LetterheadTab';
import ComplimentsSlipTab from './brand-kit/ComplimentsSlipTab';
import DownloadKitTab from './brand-kit/DownloadKitTab';

export type BrandKitTabType = 'business-name' | 'logo-design' | 'email-signature' | 'letterhead' | 'compliments-slip' | 'download-kit';

// Shared state interface for data flow between tabs
export interface BrandKitState {
  businessName: string;
  tagline: string;
  industry: string;
  description: string;
  logoUrl: string;
  logoPrompt: string;
  logoDataUrl?: string;
  primaryColor: string;
  accentColor: string;
  // Email signature data
  fullName: string;
  title: string;
  company: string;
  email: string;
  phone: string;
  website: string;
  address: string;
  socials: {
    linkedin?: string;
    twitter?: string;
    github?: string;
    facebook?: string;
    instagram?: string;
  };
  signatureHtml: string;
}

const BrandKitCreator: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [activeTab, setActiveTab] = useState<BrandKitTabType>('business-name');
  
  // Centralized state for data flow between tabs
  const [brandKit, setBrandKit] = useState<BrandKitState>({
    businessName: '',
    tagline: '',
    industry: '',
    description: '',
    logoUrl: '',
    logoPrompt: '',
    primaryColor: '#0ea5e9',
    accentColor: '#111827',
    fullName: '',
    title: '',
    company: '',
    email: '',
    phone: '',
    website: '',
    address: '',
    socials: {},
    signatureHtml: '',
  });

  // Parse hash from URL to set active tab
  useEffect(() => {
    const hash = location.hash.slice(1) as BrandKitTabType;
    if (['business-name', 'logo-design', 'email-signature', 'letterhead', 'compliments-slip', 'download-kit'].includes(hash)) {
      setActiveTab(hash);
    } else {
      setActiveTab('business-name');
    }
  }, [location.hash]);

  // Update URL hash when tab changes
  const handleTabChange = (tab: BrandKitTabType) => {
    setActiveTab(tab);
    navigate(`#${tab}`, { replace: true });
  };

  // Update brand kit state (called by child tabs)
  const updateBrandKit = (updates: Partial<BrandKitState>) => {
    setBrandKit(prev => ({ ...prev, ...updates }));
  };

  // All tabs are independently accessible - no restrictions
  const canProceedTo = (tab: BrandKitTabType): boolean => {
    return true; // All tabs accessible individually
  };

  // Tab completion status
  const isTabComplete = (tab: BrandKitTabType): boolean => {
    switch (tab) {
      case 'business-name':
        return !!brandKit.businessName && !!brandKit.tagline;
      case 'logo-design':
        return !!brandKit.logoUrl;
      case 'email-signature':
        return !!brandKit.signatureHtml;
      case 'letterhead':
        return true; // Letterhead is optional
      case 'compliments-slip':
        return true; // Compliments slip is optional
      case 'download-kit':
        return true;
      default:
        return false;
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Please sign in</h2>
          <p className="text-gray-600 mb-6">You need to be signed in to use the Brand Kit Creator</p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-3 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2 inline-flex items-center gap-3">
                <span className="text-4xl">🎨</span>
                Brand Kit Creator
              </h1>
              <p className="text-gray-600 text-lg">
                Create your complete brand identity in one place - business name, logo, and email signature
              </p>
            </div>
            <Link
              to="/help?article=brand-kit-getting-started"
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
            >
              ❓ Help
            </Link>
          </div>

          {/* Progress indicator */}
          <div className="flex items-center gap-2 mt-4">
            <div className={`flex-1 h-2 rounded-full transition ${isTabComplete('business-name') ? 'bg-orange-500' : 'bg-gray-200'}`} />
            <div className={`flex-1 h-2 rounded-full transition ${isTabComplete('logo-design') ? 'bg-orange-500' : 'bg-gray-200'}`} />
            <div className={`flex-1 h-2 rounded-full transition ${isTabComplete('email-signature') ? 'bg-orange-500' : 'bg-gray-200'}`} />
            <div className={`flex-1 h-2 rounded-full transition ${isTabComplete('letterhead') ? 'bg-orange-500' : 'bg-gray-200'}`} />
            <div className={`flex-1 h-2 rounded-full transition ${isTabComplete('compliments-slip') ? 'bg-orange-500' : 'bg-gray-200'}`} />
            <div className={`flex-1 h-2 rounded-full transition ${isTabComplete('download-kit') ? 'bg-orange-500' : 'bg-gray-200'}`} />
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="rounded-2xl bg-white shadow-sm ring-1 ring-gray-100">
          <div className="border-b border-gray-200 px-6">
            <nav className="flex gap-6 overflow-x-auto" aria-label="Tabs">
              <button
                onClick={() => handleTabChange('business-name')}
                className={`
                  group inline-flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition whitespace-nowrap
                  ${activeTab === 'business-name'
                    ? 'border-orange-600 text-orange-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }
                `}
                aria-current={activeTab === 'business-name' ? 'page' : undefined}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                <span>Business Name</span>
                {isTabComplete('business-name') && <span className="text-green-500">✓</span>}
              </button>
              
              <button
                onClick={() => handleTabChange('logo-design')}
                disabled={!canProceedTo('logo-design')}
                className={`
                  group inline-flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition whitespace-nowrap
                  ${activeTab === 'logo-design'
                    ? 'border-orange-600 text-orange-600'
                    : canProceedTo('logo-design')
                    ? 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    : 'border-transparent text-gray-300 cursor-not-allowed'
                  }
                `}
                aria-current={activeTab === 'logo-design' ? 'page' : undefined}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>Logo Design</span>
                {isTabComplete('logo-design') && <span className="text-green-500">✓</span>}
              </button>
              
              <button
                onClick={() => handleTabChange('email-signature')}
                disabled={!canProceedTo('email-signature')}
                className={`
                  group inline-flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition whitespace-nowrap
                  ${activeTab === 'email-signature'
                    ? 'border-orange-600 text-orange-600'
                    : canProceedTo('email-signature')
                    ? 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    : 'border-transparent text-gray-300 cursor-not-allowed'
                  }
                `}
                aria-current={activeTab === 'email-signature' ? 'page' : undefined}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span>Email Signature</span>
                {isTabComplete('email-signature') && <span className="text-green-500">✓</span>}
              </button>
              
              <button
                onClick={() => handleTabChange('letterhead')}
                disabled={!canProceedTo('letterhead')}
                className={`
                  group inline-flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition whitespace-nowrap
                  ${activeTab === 'letterhead'
                    ? 'border-orange-600 text-orange-600'
                    : canProceedTo('letterhead')
                    ? 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    : 'border-transparent text-gray-300 cursor-not-allowed'
                  }
                `}
                aria-current={activeTab === 'letterhead' ? 'page' : undefined}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>Letterhead</span>
                {isTabComplete('letterhead') && <span className="text-green-500">✓</span>}
              </button>
              
              <button
                onClick={() => handleTabChange('compliments-slip')}
                disabled={!canProceedTo('compliments-slip')}
                className={`
                  group inline-flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition whitespace-nowrap
                  ${activeTab === 'compliments-slip'
                    ? 'border-orange-600 text-orange-600'
                    : canProceedTo('compliments-slip')
                    ? 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    : 'border-transparent text-gray-300 cursor-not-allowed'
                  }
                `}
                aria-current={activeTab === 'compliments-slip' ? 'page' : undefined}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                </svg>
                <span>Compliments Slip</span>
                {isTabComplete('compliments-slip') && <span className="text-green-500">✓</span>}
              </button>
              
              <button
                onClick={() => handleTabChange('download-kit')}
                disabled={!canProceedTo('download-kit')}
                className={`
                  group inline-flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition whitespace-nowrap
                  ${activeTab === 'download-kit'
                    ? 'border-orange-600 text-orange-600'
                    : canProceedTo('download-kit')
                    ? 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    : 'border-transparent text-gray-300 cursor-not-allowed'
                  }
                `}
                aria-current={activeTab === 'download-kit' ? 'page' : undefined}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                </svg>
                <span>Download Kit</span>
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'business-name' && (
              <BusinessNameTab 
                brandKit={brandKit} 
                updateBrandKit={updateBrandKit}
                onNext={() => handleTabChange('logo-design')}
              />
            )}
            {activeTab === 'logo-design' && (
              <LogoDesignTab 
                brandKit={brandKit} 
                updateBrandKit={updateBrandKit}
                onNext={() => handleTabChange('email-signature')}
                onBack={() => handleTabChange('business-name')}
              />
            )}
            {activeTab === 'email-signature' && (
              <EmailSignatureTab 
                brandKit={brandKit} 
                updateBrandKit={updateBrandKit}
                onNext={() => handleTabChange('letterhead')}
                onBack={() => handleTabChange('logo-design')}
              />
            )}
            {activeTab === 'letterhead' && (
              <LetterheadTab 
                brandKit={brandKit} 
                updateBrandKit={updateBrandKit}
                onNext={() => handleTabChange('compliments-slip')}
                onBack={() => handleTabChange('email-signature')}
              />
            )}
            {activeTab === 'compliments-slip' && (
              <ComplimentsSlipTab 
                brandKit={brandKit} 
                updateBrandKit={updateBrandKit}
                onNext={() => handleTabChange('download-kit')}
                onBack={() => handleTabChange('letterhead')}
              />
            )}
            {activeTab === 'download-kit' && (
              <DownloadKitTab 
                brandKit={brandKit}
                onBack={() => handleTabChange('compliments-slip')}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandKitCreator;
